import { Button, Col, Form, Modal, Row } from "antd";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { CloseOutlined } from "@ant-design/icons";
import { RiExportFill } from "react-icons/ri";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import useGet from "../../../global/hooks/useGet";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { useEffect, useState } from "react";
import usePost from "../../../global/hooks/usePost";
import downloadCsv from "../../../global/utils/downloadCsv";
import dayjs from "dayjs";
import { FaClock, FaUser } from "react-icons/fa6";

export const OnSiteRegReportForm = ({ url, modalOpen, setModalOpen }) => {
  const [form] = Form.useForm();
  const [OemNo, setOemNo] = useState(null);
  const [defaultData, setDefaultData] = useState({
    DealerNo: null,
    OemNo: null,
  });

  const dealerFilterType = Form.useWatch("Type", form);
  const timeFilterType = Form.useWatch("TimeType", form);

  const optionUrl = `${process.env.REACT_APP_API_URL}/api/devices/options/`;
  const { data: optionsData, loading: optionDataLoading } = useGet(optionUrl);
  const modifiedOems = getModifiedResponse(optionsData, "oems");

  const {
    data: exportData,
    postData,
    error: postError,
    loading: postLoading,
  } = usePost(url);

  const dealerOptionsUrl =
    OemNo || defaultData?.OemNo
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${
          OemNo ? OemNo : defaultData?.OemNo
        }/dealers/`
      : null;
  const { data: dealerOp, refetch: dealerOpRefetch } = useGet(dealerOptionsUrl);

  const buildExportPayload = (values) => {
    const payload = {
      Type: values.Type,
      TimeType: values.TimeType,
    };

    if (values.Type === "oem" || values.Type === "dealer") {
      payload.OemNo = values.OemNo;
    }
    if (values.Type === "dealer") {
      payload.DealerNo = values.DealerNo;
    }

    if (values.TimeType === "month" && values.Month) {
      const m = values.Month;
      payload.Month = dayjs.isDayjs(m)
        ? m.format("YYYY-MM")
        : new Date(m).toISOString().slice(0, 7);
    }

    if (values.TimeType === "time") {
      if (values.StartTime) {
        payload.StartTime = dayjs(values.StartTime).format("YYYY-MM-DD HH:mm");
      }
      if (values.EndTime) {
        payload.EndTime = dayjs(values.EndTime).format("YYYY-MM-DD HH:mm");
      }
    }

    return payload;
  };

  const handleExportClick = async (values) => {
    try {
      await postData(buildExportPayload(values));
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
  };

  useEffect(() => {
    dealerOpRefetch();
  }, [OemNo]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong.",
      );
    } else if (postData && !postLoading) {
      if (
        exportData?.message === "No devices found." ||
        (exportData?.success === false && exportData?.message === "No data")
      ) {
        return openErrorNotification("No Data Found");
      }
      setTimeout(() => {
        if (exportData) {
          downloadCsv(
            exportData,
            window.location,
            `On Site Registration Report (${dayjs().format("YYYY-MM-DD")})`,
          );
          openConfirmNotification("Success");
        }
      }, 100);
    }
  }, [exportData, postError, postLoading]);

  const onDealerTypeChange = (value) => {
    if (value === "all") {
      form.setFieldsValue({ OemNo: undefined, DealerNo: undefined });
      setOemNo(null);
      setDefaultData({ OemNo: null, DealerNo: null });
    } else if (value === "oem") {
      form.setFieldsValue({ DealerNo: undefined });
      setDefaultData((prev) => ({ ...prev, DealerNo: null }));
    }
  };

  const onTimeTypeChange = (value) => {
    if (value === "month" && !form.getFieldValue("Month")) {
      form.setFieldValue("Month", dayjs());
    }
    if (value === "all") {
      form.setFieldsValue({
        Month: undefined,
        StartTime: undefined,
        EndTime: undefined,
      });
    }
    if (value === "month") {
      form.setFieldsValue({ StartTime: undefined, EndTime: undefined });
    }
    if (value === "time") {
      form.setFieldsValue({ Month: undefined });
    }
  };

  const resetAndClose = () => {
    setModalOpen(false);
    setDefaultData({ DealerNo: null, OemNo: null });
    setOemNo(null);
    form.resetFields();
  };

  const showOemRow =
    dealerFilterType === "oem" || dealerFilterType === "dealer";
  const showDealerRow = dealerFilterType === "dealer";
  const showMonthRow = timeFilterType === "month";
  const showTimeRangeRow = timeFilterType === "time";

  return (
    <Modal
      title={
        <div className="flex items-center bg-gray-200 p-2 rounded-md">
          <RiExportFill className="text-[20px] text-black pl-1" />
          <span className="text-[14px] font-[Poppins] ml-2">
            On Site Registration Report
          </span>
        </div>
      }
      open={modalOpen}
      closable={false}
      onCancel={resetAndClose}
      footer={null}
      width={580}
      centered
      destroyOnClose
    >
      <Form
        validateTrigger="onChange"
        form={form}
        initialValues={{ Type: "all", TimeType: "all" }}
        onFinish={(values) => setTimeout(() => handleExportClick(values), 200)}
        layout="vertical"
        autoComplete="off"
      >
        {/* Dealer Section */}
        <div className="overflow-hidden border bg-white rounded-[4px]">
          <div className="flex items-center bg-gray-200 p-2 rounded-[4px]">
            <FaUser className="text-black ml-1" />
            <span className="text-[14px] ml-2">Dealer</span>
          </div>
          <div className="px-2 pt-1 pb-3">
            <Row gutter={[0, 0]}>
              <Col span={24}>
                <CustomSelect
                  label="Select Type"
                  name="Type"
                  options={[
                    { label: "All", value: "all" },
                    { label: "OEM", value: "oem" },
                    { label: "Dealer", value: "dealer" },
                  ]}
                  placeholder="Please select"
                  onChange={onDealerTypeChange}
                  required
                  allowClear={false}
                />
              </Col>
              {showOemRow && (
                <Col span={24}>
                  <CustomSelect
                    label="OEM"
                    name="OemNo"
                    options={modifiedOems}
                    placeholder="Please select"
                    loading={optionDataLoading}
                    required={showOemRow}
                    onChange={(value) => {
                      setDefaultData((prev) => ({
                        ...prev,
                        OemNo: value,
                        DealerNo: null,
                      }));
                      setOemNo(value);
                      form.setFieldsValue({ DealerNo: null });
                    }}
                  />
                </Col>
              )}
              {showDealerRow && (
                <Col span={24}>
                  <CustomSelect
                    label="Dealer"
                    name="DealerNo"
                    required={showDealerRow}
                    options={
                      dealerOp?.data?.map((item) => ({
                        label: item.name,
                        value: item.value,
                      })) || []
                    }
                    disabled={!OemNo && !defaultData?.OemNo}
                    placeholder="Please select"
                    onChange={(value) =>
                      setDefaultData((prev) => ({ ...prev, DealerNo: value }))
                    }
                  />
                </Col>
              )}
            </Row>
          </div>
        </div>

        {/* Registration Time Section */}
        <div className="overflow-hidden border bg-white rounded-[4px] mt-2.5">
          <div className="flex items-center bg-gray-200 p-2 rounded-[4px]">
            <FaClock className="text-black" />
            <span className="text-[14px] ml-2">Registration Time</span>
          </div>
          <div className="px-2 pt-1 pb-3">
            <Row gutter={[0, 0]}>
              <Col span={24}>
                <CustomSelect
                  label="Select Type"
                  name="TimeType"
                  options={[
                    { label: "All", value: "all" },
                    { label: "Month", value: "month" },
                    { label: "Time", value: "time" },
                  ]}
                  placeholder="Please select"
                  onChange={onTimeTypeChange}
                  required
                  allowClear={false}
                />
              </Col>
              {showMonthRow && (
                <Col span={24}>
                  <CustomDateTimePicker
                    name="Month"
                    label="Month"
                    placeholder="Please select"
                    picker="month"
                    required={showMonthRow}
                    disableFuture
                  />
                </Col>
              )}
              {showTimeRangeRow && (
                <>
                  <Col span={24}>
                    <CustomDateTimePicker
                      name="StartTime"
                      label="Start Time"
                      placeholder="Please select"
                      required={showTimeRangeRow}
                      disableFuture
                    />
                  </Col>
                  <Col span={24}>
                    <CustomDateTimePicker
                      name="EndTime"
                      label="End Time"
                      placeholder="Please select"
                      required={showTimeRangeRow}
                      disableFuture
                      dependencies={["StartTime"]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const start = getFieldValue("StartTime");
                            if (!value || !start) return Promise.resolve();
                            if (dayjs(value).isBefore(dayjs(start))) {
                              return Promise.reject(
                                new Error(
                                  "End time cannot be before start time",
                                ),
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    />
                  </Col>
                </>
              )}
            </Row>
          </div>
        </div>

        <div className="flex justify-center space-x-2 mt-2.5">
          <Button
            type="primary"
            className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
            onClick={() => form.submit()}
            loading={postLoading}
          >
            <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
            <span>Apply</span>
          </Button>
          <Button className="reset-button" onClick={resetAndClose}>
            <CloseOutlined />
            <span>Cancel</span>
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
